/**
 * api.ts — Member Registration
 *
 * Wrappeurs AJAX/REST pour l'inscription et la vérification de compte.
 * Priorité REST si api_transport === 'rest' et rest_nonce présent,
 * fallback AJAX sinon (phase transitoire).
 */

import { gasFetch } from '../../shared/kit';
import type { EmailAvailableResponse, MemberRegistrationAjaxResponse } from './types';

export interface RegistrationApiConfig {
    ajaxUrl?: string;
    nonce?: string;
    rest_nonce?: string;
    rest_base_url?: string;
    api_transport?: 'rest' | 'ajax';
}

// =========================================
// Helpers internes
// =========================================

function canUseRest(cfg: RegistrationApiConfig): boolean {
    return cfg.api_transport !== 'ajax' && String(cfg.rest_nonce || '').trim() !== '';
}

function normalizeBaseUrl(value: string): string {
    return value.replace(/\/+$/, '');
}

function buildRestUrl(cfg: RegistrationApiConfig, path: string): string {
    const baseUrl = normalizeBaseUrl(String(cfg.rest_base_url || '/wp-json/gas/v1'));
    return baseUrl + path;
}

async function restPost<TData>(
    cfg: RegistrationApiConfig,
    path: string,
    data: Record<string, unknown>,
): Promise<TData> {
    const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    if (cfg.rest_nonce) {
        headers['X-WP-Nonce'] = cfg.rest_nonce;
    }

    const response = await fetch(buildRestUrl(cfg, path), {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(data),
    });

    let parsed: Record<string, unknown> = {};
    try {
        parsed = (await response.json()) as Record<string, unknown>;
    } catch (_err) {
        if (!response.ok) {
            throw new Error('Erreur réseau lors de la requête REST.');
        }
    }

    if (!response.ok) {
        // Extraire le message d'erreur et les erreurs de champs si présentes
        const message = typeof parsed.message === 'string' ? parsed.message : 'Erreur lors de la requête.';
        const extraData = typeof parsed.data === 'object' && parsed.data !== null ? (parsed.data as Record<string, unknown>) : {};
        const err = Object.assign(new Error(message), { data: extraData });
        throw err;
    }

    if (!Object.prototype.hasOwnProperty.call(parsed, 'data')) {
        throw new Error('Réponse REST invalide (enveloppe manquante).');
    }

    return parsed.data as TData;
}

// =========================================
// Fonctions publiques
// =========================================

/**
 * Inscription d'un nouvel utilisateur.
 * REST : POST /gas/v1/inscription
 * Fallback AJAX : action gas_user_registration
 */
export async function registerMember(
    cfg: RegistrationApiConfig,
    data: Record<string, unknown>,
): Promise<MemberRegistrationAjaxResponse> {
    if (canUseRest(cfg)) {
        try {
            return await restPost<MemberRegistrationAjaxResponse>(cfg, '/inscription', data);
        } catch (err) {
            // Fallback AJAX uniquement si le transport est en mode automatique
            // (pas de refus délibéré) et que l'URL AJAX est disponible.
            if (cfg.api_transport === 'rest' || !cfg.ajaxUrl) {
                throw err;
            }
            // fallback
        }
    }

    return gasFetch<MemberRegistrationAjaxResponse>({
        action: 'gas_user_registration',
        ajaxUrl: cfg.ajaxUrl || '',
        nonce: cfg.nonce || '',
        data,
    });
}

/**
 * Migration d'un compte WP existant → membre APH.
 * REST : POST /gas/v1/inscription/verification
 * Fallback AJAX : action gas_account_verification
 */
export async function verifyAccount(
    cfg: RegistrationApiConfig,
    data: Record<string, unknown>,
): Promise<MemberRegistrationAjaxResponse> {
    if (canUseRest(cfg)) {
        try {
            return await restPost<MemberRegistrationAjaxResponse>(cfg, '/inscription/verification', data);
        } catch (err) {
            if (cfg.api_transport === 'rest' || !cfg.ajaxUrl) {
                throw err;
            }
            // fallback
        }
    }

    return gasFetch<MemberRegistrationAjaxResponse>({
        action: 'gas_account_verification',
        ajaxUrl: cfg.ajaxUrl || '',
        nonce: cfg.nonce || '',
        data,
    });
}

/**
 * Vérifie si un email est libre pour une inscription.
 * REST : GET /gas/v1/inscription/email-available?email=
 * Pas de fallback AJAX (endpoint neuf REST-only).
 */
export async function checkEmailAvailable(
    cfg: RegistrationApiConfig,
    email: string,
): Promise<EmailAvailableResponse> {
    const headers: Record<string, string> = {
        Accept: 'application/json',
    };
    if (cfg.rest_nonce) {
        headers['X-WP-Nonce'] = cfg.rest_nonce;
    }

    const url = buildRestUrl(cfg, '/inscription/email-available')
        + '?email=' + encodeURIComponent(email);

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers,
    });

    let parsed: Record<string, unknown> = {};
    try {
        parsed = (await response.json()) as Record<string, unknown>;
    } catch (_err) {
        if (!response.ok) {
            throw new Error('Erreur réseau lors de la vérification email.');
        }
    }

    if (!response.ok) {
        const message = typeof parsed.message === 'string' ? parsed.message : 'Erreur lors de la vérification email.';
        throw new Error(message);
    }

    if (!Object.prototype.hasOwnProperty.call(parsed, 'data')) {
        throw new Error('Réponse REST invalide (enveloppe manquante).');
    }

    return parsed.data as EmailAvailableResponse;
}
